import {
  Card,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  MoreVertRounded
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RRLink, NavLink } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { ORDER } from '../../../common/enum/order';
import { PaginationProps } from '../../../common/interface/pagination';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import { Discipline, getDisciplinesMasterDataAsync } from './disciplineSlice';

interface DisciplineParamProps extends PaginationProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      borderRadius: '16px',
      display: 'flex',
      padding: theme.spacing(1, 1, 1, 2),
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0
      }
    },
    cardContainer: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    width50: {
      width: '50%',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      paddingRight: theme.spacing(3),
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 100
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    }
  })
);

const sortOptions = [
  { value: 'id', text: 'createdDate' },
  { value: 'name', text: 'name' }
];

export default function DisciplineView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const { masterData: disciplines, count } = useSelector(
    (state: RootState) => state.disciplines
  );

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [selectedDiscipline, setSelectedDiscipline] =
    useState<Discipline | null>(null);
  const [disciplineParams, setDisciplineParams] =
    useState<DisciplineParamProps>({
      page: 0,
      limit: 30,
      sortBy: 'name',
      orderBy: ORDER.asc
    });

  useEffect(() => {
    loadingDiscipline(disciplineParams);
  }, []);

  function loadingDiscipline(params: DisciplineParamProps) {
    dispatch(getDisciplinesMasterDataAsync(params));
    setDisciplineParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingDiscipline({
      ...disciplineParams,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function toggleDrawer(open: boolean, discipline?: Discipline) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && discipline) {
        setSelectedDiscipline(discipline);
      }
      setOpenOption(open);
    };
  }

  return (
    <div className={classes.root}>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText primary={selectedDiscipline?.name} />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem
              component={NavLink}
              to={`disciplines/${selectedDiscipline?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('masterData.discipline.actions.edit')} />
            </ListItem>
            <ListItem button onClick={() => {}} disabled>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('masterData.discipline.actions.delete')}
                secondary={t('projectCard.functionNotAvailable')}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <Breadcrumb
        name="masterDataName"
        data={{
          masterDataName: t('masterData.discipline.title'),
          masterDataRef: 'disciplines'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('masterData.discipline.title')}
      </Typography>
      <EnhancedTableToolbar
        name={t('masterData.discipline.title')}
        params={disciplineParams}
        sortOptions={sortOptions.map((s) => ({ ...s, text: t(`masterData.sortOptions.${s.text}`) }))}
        loadingElement={loadingDiscipline}
      />
      {disciplines.map((discipline) => {
        return (
          <Card className={classes.card} key={discipline.id}>
            <div className={classes.cardContainer}>
              <div className={classes.width50}>
                <Typography>{discipline.name}</Typography>
              </div>
              <div className={classes.width50}>
                <Typography>{discipline.cfihosName}</Typography>
              </div>
            </div>
            <div>
              <IconButton size="small" onClick={toggleDrawer(true, discipline)}>
                <MoreVertRounded />
              </IconButton>
            </div>
          </Card>
        );
      })}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / disciplineParams.limit)}
          shape="rounded"
          page={disciplineParams.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.fab}
          component={RRLink}
          to={`disciplines/create`}
          variant="extended"
          color="primary"
          aria-label={t('masterData.discipline.actions.newAria') as string}
        >
          <AddRounded className={classes.marginRight1} />
          {t('masterData.discipline.actions.new')}
        </Fab>
      </Zoom>
    </div>
  );
}
